import { ANALYTICS_PRESET, COMPARE_TO } from '@pfsc/core/services/analytics.service';

/**
 * The windows the Compare tab offers, each paired with what it is measured against.
 *
 * Kept apart from the button row so the dashboard can tell whether the period it holds is one of
 * them, and fall back to a default when it is not.
 */

/**
 * Format a date as the API expects it, in local wall-clock terms.
 * @param {Date} date Date.
 * @returns {string} Y-m-d
 */
function toDateParam(date) {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Build a calendar-aligned window.
 * @param {Date} start Inclusive start.
 * @param {Date} end Inclusive end.
 * @returns {{startDate: string, endDate: string}}
 */
function range(start, end) {
  return { startDate: toDateParam(start), endDate: toDateParam(end) };
}

/**
 * Every Compare window, as of a day.
 * @param {Date} today The day the windows are measured from.
 * @returns {object[]}
 */
export function compareOptions(today = new Date()) {
  const year = today.getFullYear();
  const month = today.getMonth();

  // Weeks run Monday to Sunday, which is how the business reads a trading week.
  const dayOffset = (today.getDay() + 6) % 7;
  const weekStart = new Date(year, month, today.getDate() - dayOffset);
  const lastWeekStart = new Date(year, month, weekStart.getDate() - 7);

  return [
    {
      key: 'this_week',
      label: 'This week',
      caption: 'vs the same days last week',
      ...range(weekStart, today),
      compareTo: COMPARE_TO.SAME_SPAN_LAST_WEEK,
    },
    {
      key: 'last_week',
      label: 'Last week',
      caption: 'vs the week before',
      ...range(lastWeekStart, new Date(year, month, weekStart.getDate() - 1)),
      compareTo: COMPARE_TO.PREVIOUS_PERIOD,
    },
    {
      key: 'this_month',
      label: 'This month',
      caption: 'vs the same span last month',
      ...range(new Date(year, month, 1), today),
      compareTo: COMPARE_TO.SAME_SPAN_LAST_MONTH,
    },
    {
      key: 'last_month',
      label: 'Last month',
      caption: 'vs the month before',
      ...range(new Date(year, month - 1, 1), new Date(year, month, 0)),
      compareTo: COMPARE_TO.PREVIOUS_CALENDAR,
    },
    {
      key: 'this_quarter',
      label: 'This quarter',
      caption: 'vs the same span last quarter',
      ...range(new Date(year, Math.floor(month / 3) * 3, 1), today),
      compareTo: COMPARE_TO.SAME_SPAN_LAST_QUARTER,
    },
    {
      key: 'ytd',
      label: 'Year to date',
      caption: 'vs the same span last year',
      ...range(new Date(year, 0, 1), today),
      compareTo: COMPARE_TO.SAME_SPAN_LAST_YEAR,
    },
    {
      key: 'last_year',
      label: 'Last year',
      caption: 'vs the year before',
      ...range(new Date(year - 1, 0, 1), new Date(year - 1, 11, 31)),
      compareTo: COMPARE_TO.PREVIOUS_CALENDAR,
    },
  ];
}

/**
 * The period a Compare option stands for.
 * @param {object} option Option from compareOptions().
 * @returns {object}
 */
export function periodOf(option) {
  return {
    preset: ANALYTICS_PRESET.CUSTOM,
    startDate: option.startDate,
    endDate: option.endDate,
    compareTo: option.compareTo,
  };
}

/**
 * Whether an option describes a period exactly.
 * @param {object} option Option from compareOptions().
 * @param {object} period Period.
 * @returns {boolean}
 */
export function matchesOption(option, period) {
  return period.preset === ANALYTICS_PRESET.CUSTOM
    && period.startDate === option.startDate
    && period.endDate === option.endDate
    && (period.compareTo ?? COMPARE_TO.PREVIOUS_PERIOD) === option.compareTo;
}

/**
 * Whether a period is one of the Compare tab's own windows.
 * @param {object} period Period.
 * @returns {boolean}
 */
export function isCompareWindow(period) {
  return compareOptions().some(option => matchesOption(option, period));
}

/**
 * The window Compare opens on when the period it inherits is not one of its own: this month
 * against the same span last month.
 * @returns {object} Period.
 */
export function defaultComparePeriod() {
  return periodOf(compareOptions().find(option => option.key === 'this_month'));
}
